import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Joystick from '../components/shared/Joystick';
import { GridFisheyeCanvas } from '../components/shared/GridFisheyeCanvas';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { adminApi } from '../api/admin';
import { resolveBackendAssetUrl } from '../utils/assetUrl';
import { useControlPanelWs } from '../websocket/controlPanelWs';
import type {
  Project,
  Device,
  ControlCategory,
  ControlButton,
  JoystickConfig,
  FloorPlan,
  TeleportPoint,
} from '../types';

type ControlPanelPage = 'joysticks' | 'actions' | 'floorplan';

const DEFAULT_JOYSTICK_CONFIG: JoystickConfig = {
  movementEnabled: true,
  cameraEnabled: true,
  movementSensitivity: 1,
  cameraSensitivity: 1,
};

const ControlPanel: React.FC = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const [devices, setDevices] = useState<Device[]>([]);
  const [categories, setCategories] = useState<ControlCategory[]>([]);
  const [buttons, setButtons] = useState<ControlButton[]>([]);
  const [joystickConfig, setJoystickConfig] = useState<JoystickConfig>(DEFAULT_JOYSTICK_CONFIG);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [selectedDevice, setSelectedDevice] = useState<string>('');
  const [movementValues, setMovementValues] = useState({ x: 0, y: 0 });
  const [cameraValues, setCameraValues] = useState({ x: 0, y: 0 });
  const [showJoystickValues, setShowJoystickValues] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [controlPanelPage, setControlPanelPage] = useState<ControlPanelPage>('joysticks');
  const [mouseSpotlight, setMouseSpotlight] = useState(() =>
    typeof window !== 'undefined'
      ? { x: window.innerWidth / 2, y: window.innerHeight / 2 }
      : { x: 0, y: 0 }
  );

  const [floorPlans, setFloorPlans] = useState<FloorPlan[]>([]);
  const [selectedFloorPlanId, setSelectedFloorPlanId] = useState<string | null>(null);
  const [teleportPoints, setTeleportPoints] = useState<TeleportPoint[]>([]);

  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { connected, connecting, connectedDeviceIds, send, requestDeviceList, error } = useControlPanelWs(selectedProjectId);
  const initialProjectSet = useRef(false);

  useEffect(() => {
    document.title = 'Control Panel';
    return () => { document.title = 'VR Companion Admin'; };
  }, []);

  // Restore last opened sub-page (joysticks / actions / floorplan) on refresh.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const stored = window.localStorage.getItem('vrcompanion.controlPanelPage');
    if (stored === 'joysticks' || stored === 'actions' || stored === 'floorplan') {
      setControlPanelPage(stored);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoadError(null);
    adminApi.projects
      .list()
      .then((list) => {
        if (!cancelled) {
          const arr = Array.isArray(list) ? list : [];
          setProjects(arr);
          if (arr.length > 0 && !initialProjectSet.current) {
            initialProjectSet.current = true;
            setSelectedProjectId(arr[0].id);
          }
        }
      })
      .catch((err) => {
        if (!cancelled) setLoadError(err?.message || 'Failed to load projects');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!selectedProjectId) {
      setDevices([]);
      setCategories([]);
      setButtons([]);
      setJoystickConfig(DEFAULT_JOYSTICK_CONFIG);
      setSelectedDevice('');
      setFloorPlans([]);
      setSelectedFloorPlanId(null);
      setTeleportPoints([]);
      return;
    }
    let cancelled = false;
    Promise.all([
      adminApi.devices.list(selectedProjectId),
      adminApi.controls.categories.list(selectedProjectId),
      adminApi.controls.buttons.listByProject(selectedProjectId),
      adminApi.controls.joystickConfig.get(selectedProjectId).catch(() => null),
      adminApi.floorPlans.listByProject(selectedProjectId).catch(() => [] as FloorPlan[]),
      adminApi.teleportPoints.listByProject(selectedProjectId).catch(
        () => [] as TeleportPoint[]
      ),
    ])
      .then(([devList, catList, btnList, config, floorList, pointsList]) => {
        if (cancelled) return;
        const devArr = Array.isArray(devList) ? devList : [];
        const catArr = Array.isArray(catList) ? catList : [];
        const btnArr = Array.isArray(btnList) ? btnList : [];
        const floorArr = Array.isArray(floorList) ? floorList : [];
        setDevices(devArr);
        setCategories(catArr);
        setButtons(btnArr);
        const cfg = config ?? DEFAULT_JOYSTICK_CONFIG;
        setJoystickConfig({
          ...DEFAULT_JOYSTICK_CONFIG,
          ...(cfg && typeof cfg === 'object' ? cfg : {}),
          movementSensitivity: typeof cfg?.movementSensitivity === 'number' ? cfg.movementSensitivity : DEFAULT_JOYSTICK_CONFIG.movementSensitivity,
          cameraSensitivity: typeof cfg?.cameraSensitivity === 'number' ? cfg.cameraSensitivity : DEFAULT_JOYSTICK_CONFIG.cameraSensitivity,
        });
        setFloorPlans(floorArr);
        setSelectedFloorPlanId((prev) => {
          const ids = floorArr.map((f) => f.id);
          if (prev && ids.includes(prev)) return prev;
          return ids[0] ?? null;
        });
        setTeleportPoints(Array.isArray(pointsList) ? pointsList : []);
        setSelectedDevice((prev) => {
          const ids = devArr.map((d) => d.id);
          if (ids.includes(prev)) return prev;
          return ids[0] ?? '';
        });
      })
      .catch((err) => {
        if (!cancelled) setLoadError(err?.message || 'Failed to load project data');
      });
    return () => {
      cancelled = true;
    };
  }, [selectedProjectId]);

  const handleMovement = useCallback(
    (x: number, y: number) => {
      setMovementValues({ x, y });
      if (selectedProjectId && selectedDevice) {
        send({
          type: 'MOVE',
          projectId: selectedProjectId,
          deviceId: selectedDevice,
          payload: { x, y },
        });
      }
      if (process.env.NODE_ENV === 'development') {
        console.log('[Joystick] Movement', { x, y });
      }
    },
    [selectedProjectId, selectedDevice, send]
  );

  const handleCamera = useCallback(
    (x: number, y: number) => {
      setCameraValues({ x, y });
      if (selectedProjectId && selectedDevice) {
        send({
          type: 'LOOK',
          projectId: selectedProjectId,
          deviceId: selectedDevice,
          payload: { x, y },
        });
      }
      if (process.env.NODE_ENV === 'development') {
        console.log('[Joystick] Camera', { x, y });
      }
    },
    [selectedProjectId, selectedDevice, send]
  );

  const handleActionButton = useCallback(
    (button: ControlButton) => {
      if (!selectedProjectId || !selectedDevice) return;
      if (button.actionType === 'TELEPORT') {
        send({
          type: 'TELEPORT',
          projectId: selectedProjectId,
          deviceId: selectedDevice,
          payload: {
            buttonId: button.id,
            position: button.position,
            rotationYaw: button.rotationYaw,
          },
        });
      } else {
        send({
          type: 'ACTION',
          projectId: selectedProjectId,
          deviceId: selectedDevice,
          payload: { buttonId: button.id },
        });
      }
      if (process.env.NODE_ENV === 'development') {
        console.log('[ControlPanel] Send', button.actionType, 'to device', selectedDevice, button.actionType === 'TELEPORT' ? { position: button.position, rotationYaw: button.rotationYaw } : {});
      }
    },
    [selectedProjectId, selectedDevice, send]
  );

  const handleTeleportPointClick = useCallback(
    (point: TeleportPoint) => {
      if (!selectedProjectId || !selectedDevice) return;
      if (point.worldX == null || point.worldY == null || point.worldZ == null) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('[ControlPanel] Teleport point has no world coordinates configured', point);
        }
        return;
      }
      send({
        type: 'TELEPORT',
        projectId: selectedProjectId,
        deviceId: selectedDevice,
        payload: {
          pointId: point.id,
          position: {
            x: point.worldX,
            y: point.worldY,
            z: point.worldZ,
          },
          ...(typeof point.worldYaw === 'number' && !Number.isNaN(point.worldYaw)
            ? { rotationYaw: point.worldYaw }
            : {}),
        },
      });
      if (process.env.NODE_ENV === 'development') {
        console.log('[ControlPanel] Teleport via floor plan', {
          pointId: point.id,
          position: { x: point.worldX, y: point.worldY, z: point.worldZ },
          rotationYaw: point.worldYaw,
        });
      }
    },
    [selectedProjectId, selectedDevice, send]
  );

  const handleDeviceChange = (deviceId: string) => {
    setSelectedDevice(deviceId);
    if (process.env.NODE_ENV === 'development') {
      console.log('[ControlPanel] Device selected', deviceId);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  const closeMenu = () => setMenuOpen(false);
  const toggleMenu = () => setMenuOpen((prev) => !prev);

  const goToPage = (page: ControlPanelPage) => {
    setControlPanelPage(page);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem('vrcompanion.controlPanelPage', page);
    }
    closeMenu();
  };

  const goToAdminPanel = () => {
    navigate('/dashboard');
    closeMenu();
  };

  const userName = user?.name?.trim() || 'Operator';
  const userEmail = user?.email?.trim() || '';
  const userId = user?.id?.trim() || '';
  const userInitial = userName.charAt(0).toUpperCase();
  const isAdmin = user?.role === 'admin';

  const safeCategories = Array.isArray(categories) ? categories : [];
  const safeButtons = Array.isArray(buttons) ? buttons : [];
  const safeFloorPlans = Array.isArray(floorPlans) ? floorPlans : [];
  const selectedFloorIndex = safeFloorPlans.findIndex((fp) => fp.id === selectedFloorPlanId);
  const selectedFloorPlan =
    selectedFloorIndex >= 0 ? safeFloorPlans[selectedFloorIndex] : null;
  const selectedFloorPoints = selectedFloorPlan
    ? teleportPoints.filter((p) => p.floorPlanId === selectedFloorPlan.id)
    : [];
  const categoriesWithButtons = safeCategories.map((cat) => ({
    category: cat,
    buttons: safeButtons.filter((b) => b && b.categoryId === cat.id),
  }));

  const safeProjects = Array.isArray(projects) ? projects : [];
  if (loading && safeProjects.length === 0) {
    return (
      <div className="cp-page min-h-screen bg-black text-white flex items-center justify-center">
        <p className="text-gray-400">Loading...</p>
      </div>
    );
  }

  const movementSensitivity = typeof joystickConfig.movementSensitivity === 'number' ? joystickConfig.movementSensitivity : DEFAULT_JOYSTICK_CONFIG.movementSensitivity;
  const cameraSensitivity = typeof joystickConfig.cameraSensitivity === 'number' ? joystickConfig.cameraSensitivity : DEFAULT_JOYSTICK_CONFIG.cameraSensitivity;

  const handlePageMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    setMouseSpotlight({ x: e.clientX, y: e.clientY });
  };

  const goToPreviousFloor = () => {
    if (selectedFloorIndex <= 0) return;
    setSelectedFloorPlanId(safeFloorPlans[selectedFloorIndex - 1].id);
  };

  const goToNextFloor = () => {
    if (selectedFloorIndex < 0 || selectedFloorIndex >= safeFloorPlans.length - 1) return;
    setSelectedFloorPlanId(safeFloorPlans[selectedFloorIndex + 1].id);
  };

  return (
    <div
      className="cp-page min-h-screen bg-gradient-to-br from-gray-950 via-gray-900 to-gray-950 text-white flex flex-col"
      onMouseMove={handlePageMouseMove}
      style={
        {
          '--mouse-x': `${mouseSpotlight.x}px`,
          '--mouse-y': `${mouseSpotlight.y}px`,
        } as React.CSSProperties
      }
    >
      {/* Grid fisheye – grid pulled toward cursor (fish-eye lens effect) */}
      <GridFisheyeCanvas mouseX={mouseSpotlight.x} mouseY={mouseSpotlight.y} theme={theme} />
      {/* Grid spotlight – follows cursor (like asrockstudios.in) */}
      <div className="cp-page-grid-spotlight" aria-hidden="true" />
      {/* Overlay when menu is open */}
      <div
        className={`cp-menu-overlay ${menuOpen ? 'cp-menu-overlay-visible' : ''}`}
        onClick={closeMenu}
        onKeyDown={(e) => e.key === 'Escape' && closeMenu()}
        role="button"
        tabIndex={-1}
        aria-hidden={!menuOpen}
        aria-label="Close menu"
      />

      {/* Hamburger menu drawer – User Profile */}
      <div className={`cp-menu-drawer ${menuOpen ? 'cp-menu-drawer-open' : ''}`}>
        <div className="cp-menu-drawer-header">
          <h2 className="cp-menu-drawer-title">Menu</h2>
          <button
            type="button"
            className="cp-menu-drawer-close"
            onClick={closeMenu}
            title="Close menu"
            aria-label="Close menu"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="cp-menu-drawer-body">
          {/* User details at top */}
          <div className="cp-menu-user-profile">
            <div className="cp-menu-user-avatar">
              <span>{userInitial}</span>
            </div>
            <h3 className="cp-menu-user-name">{userName}</h3>
            {userEmail && (
              <p className="cp-menu-user-email" title={userEmail}>
                {userEmail}
              </p>
            )}
            {userId && (
              <p className="cp-menu-user-id">ID: {userId}</p>
            )}
          </div>

          {/* Navigation below user details */}
          <nav className="cp-menu-nav" aria-label="Control panel pages">
            <button
              type="button"
              className={`cp-menu-nav-item ${controlPanelPage === 'joysticks' ? 'cp-menu-nav-item--active' : ''}`}
              onClick={() => goToPage('joysticks')}
              aria-current={controlPanelPage === 'joysticks' ? 'page' : undefined}
            >
              <svg className="cp-menu-nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="3" />
                <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41" />
              </svg>
              <span>VR Control</span>
            </button>
            <button
              type="button"
              className={`cp-menu-nav-item ${controlPanelPage === 'actions' ? 'cp-menu-nav-item--active' : ''}`}
              onClick={() => goToPage('actions')}
              aria-current={controlPanelPage === 'actions' ? 'page' : undefined}
            >
              <svg className="cp-menu-nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="7" height="7" />
                <rect x="14" y="3" width="7" height="7" />
                <rect x="14" y="14" width="7" height="7" />
                <rect x="3" y="14" width="7" height="7" />
              </svg>
              <span>Action Buttons</span>
            </button>
            <button
              type="button"
              className={`cp-menu-nav-item ${controlPanelPage === 'floorplan' ? 'cp-menu-nav-item--active' : ''}`}
              onClick={() => goToPage('floorplan')}
              aria-current={controlPanelPage === 'floorplan' ? 'page' : undefined}
            >
              <svg
                className="cp-menu-nav-icon"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="3" y="3" width="7" height="7" />
                <rect x="14" y="3" width="7" height="7" />
                <rect x="3" y="14" width="7" height="7" />
                <path d="M14 14h7v7h-7z" />
              </svg>
              <span>Floor Plan</span>
            </button>
            {isAdmin && (
              <button
                type="button"
                className="cp-menu-nav-item"
                onClick={goToAdminPanel}
              >
                <svg
                  className="cp-menu-nav-icon"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M12 2l7 4v6c0 5-3 8-7 10-4-2-7-5-7-10V6l7-4z" />
                  <path d="M10 11l2 2 4-4" />
                </svg>
                <span>Admin Panel</span>
              </button>
            )}
          </nav>

          <div className="cp-menu-theme-toggle">
            <label className="theme-switch toggle-switch" title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}>
              <input
                type="checkbox"
                checked={theme === 'light'}
                onChange={toggleTheme}
                aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
              />
              <span className="toggle-slider theme-switch-slider" />
              <span className="theme-switch-label">{theme === 'dark' ? 'Dark' : 'Light'}</span>
            </label>
          </div>

          <button
            type="button"
            className="cp-menu-logout"
            onClick={handleLogout}
            title="Log out"
            aria-label="Log out"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            <span>Log out</span>
          </button>
        </div>
      </div>

      <header className="cp-header">
        <div className="cp-header-inner">
          <div className="cp-header-bar">
            <button
              type="button"
              className="cp-hamburger"
              onClick={toggleMenu}
              title="Open menu"
              aria-label="Open menu"
              aria-expanded={menuOpen}
            >
              <svg className="cp-hamburger-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                <line x1="3" y1="6" x2="21" y2="6" />
                <line x1="3" y1="12" x2="21" y2="12" />
                <line x1="3" y1="18" x2="21" y2="18" />
              </svg>
            </button>
            <div className="cp-header-brand-stack">
              <h1 className="cp-header-app-name">VR COMPANION</h1>
              <h2 className="cp-header-page-title">
                {controlPanelPage === 'joysticks'
                  ? 'Control Panel'
                  : controlPanelPage === 'actions'
                    ? 'Action Buttons'
                    : 'Floor Plan'}
              </h2>
            </div>
            <div className="cp-header-controls">
          {loadError && (
            <div className="cp-header-error text-xs font-bold text-red-400 uppercase tracking-wider">
              {loadError}
            </div>
          )}

          {safeProjects.length > 0 && (
            <div className="cp-device-select">
              <span id="cp-project-label" className="cp-device-select-label">
                Project
              </span>
              <select
                id="cp-project-dropdown"
                aria-labelledby="cp-project-label"
                value={selectedProjectId ?? ''}
                onChange={(e) => setSelectedProjectId(e.target.value || null)}
                className="cp-device-select-dropdown"
              >
                {safeProjects.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="cp-device-select">
            <span id="cp-device-label" className="cp-device-select-label">
              Active Device
            </span>
            <p className="cp-device-select-hint text-xs text-gray-500 mt-0.5 mb-1">
              Same Device ID as in Unreal Connect. {connectedDeviceIds.size > 0 && '● = connected.'}
            </p>
            {devices.length === 0 ? (
              <div className="cp-device-select-empty" aria-live="polite">
                No devices available
              </div>
            ) : (
              <select
                id="cp-device-dropdown"
                aria-labelledby="cp-device-label"
                value={selectedDevice}
                onChange={(e) => handleDeviceChange(e.target.value)}
                className="cp-device-select-dropdown"
              >
                {devices.map((device) => (
                  <option key={device.id} value={device.id}>
                    {device.name}
                    {connectedDeviceIds.has(device.id) ? ' ●' : ''}
                  </option>
                ))}
              </select>
            )}
          </div>

          {controlPanelPage === 'joysticks' && (
            <label className="cp-header-checkbox flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={showJoystickValues}
                onChange={(e) => setShowJoystickValues(e.target.checked)}
                className="rounded border-gray-600 bg-gray-800 text-primary-500 focus:ring-primary-500"
              />
              <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">
                Show joystick values
              </span>
            </label>
          )}

          <div className="cp-header-status">
            <div className="cp-header-status-row text-xs font-bold text-gray-400 uppercase tracking-wider flex items-center gap-3">
              <span>Status</span>
              {connected && (
                <button
                  type="button"
                  onClick={requestDeviceList}
                  className="cp-header-refresh text-xs font-medium text-primary-400 hover:text-primary-300 underline"
                  title="Refresh connected devices from relay"
                >
                  Refresh
                </button>
              )}
            </div>
            <div className="cp-status-row">
              {(() => {
                const deviceOnline = selectedDevice && connectedDeviceIds.has(selectedDevice);
                const relayConnected = connected && !error;
                const showGreen = relayConnected && selectedDevice && deviceOnline;
                const showYellow = connecting || error || (relayConnected && (!selectedDevice || !deviceOnline));
                const statusDot = showGreen ? 'green' : showYellow ? 'yellow' : 'red';
                const statusLabel =
                  !connected && !connecting
                    ? 'Disconnected'
                    : connecting
                      ? 'Connecting…'
                      : error
                        ? 'Bad network'
                        : !selectedDevice
                          ? 'Select a device'
                          : !deviceOnline
                            ? 'Device offline'
                            : 'Connected';
                const statusTitle =
                  showGreen
                    ? 'Connected to relay and VR headset (Unreal)'
                    : statusLabel === 'Device offline'
                      ? 'Relay connected; selected VR headset is not connected'
                      : statusLabel === 'Select a device'
                        ? 'Relay connected; select a device to see if VR is online'
                        : statusLabel;
                return (
                  <>
                    <div
                      className={`cp-status-dot cp-status-dot--${statusDot}`}
                      title={statusTitle}
                      aria-hidden
                    />
                    <span className={`text-sm font-bold cp-status-label--${statusDot}`}>
                      {statusLabel}
                    </span>
                  </>
                );
              })()}
            </div>
          </div>
        </div>
        </div>
        </div>
      </header>

      <main className="cp-main flex-1 overflow-auto cp-main-tight">
        <div className="cp-main-inner">
          {safeProjects.length === 0 && !loading ? (
            <p className="text-gray-400">No projects available.</p>
          ) : !selectedProjectId ? (
            <p className="text-gray-400">Select a project to start.</p>
          ) : controlPanelPage === 'joysticks' ? (
            <>
              <section className="cp-joysticks-section">
                <p className="text-sm text-gray-400 cp-joysticks-section-desc">Move and look in VR. Select device above first.</p>
                <div className="cp-joysticks-row">
                  <div className="cp-joystick-card">
                    <h3 className="text-lg font-bold text-primary-400">Movement</h3>
                    <div className="text-xs text-gray-400 text-center mb-2">
                      <p>Drag to move</p>
                      <p className="text-gray-500">Forward • Backward • Left • Right</p>
                    </div>
                    <Joystick
                      label="Movement"
                      onMove={handleMovement}
                      debug={showJoystickValues}
                      sensitivity={movementSensitivity}
                    />
                  </div>

                  <div className="cp-joystick-card">
                    <h3 className="text-lg font-bold text-primary-400">Camera</h3>
                    <div className="text-xs text-gray-400 text-center mb-2">
                      <p>Drag to look</p>
                      <p className="text-gray-500">Look • Rotate • Aim</p>
                    </div>
                    <Joystick
                      label="Camera"
                      onMove={handleCamera}
                      debug={showJoystickValues}
                      sensitivity={cameraSensitivity}
                    />
                  </div>
                </div>
              </section>

              <section className="cp-debug-info cp-glass mt-8 rounded-lg border-2 border-gray-700 p-4">
                <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">
                  Debug Info
                </h3>
                <div className="grid grid-cols-4 gap-4 text-xs font-mono">
                  <div>
                    <span className="text-gray-400">Movement X:</span>
                    <p className="text-primary-400 font-bold">{movementValues.x.toFixed(2)}</p>
                  </div>
                  <div>
                    <span className="text-gray-400">Movement Y:</span>
                    <p className="text-primary-400 font-bold">{movementValues.y.toFixed(2)}</p>
                  </div>
                  <div>
                    <span className="text-gray-400">Camera X:</span>
                    <p className="text-primary-400 font-bold">{cameraValues.x.toFixed(2)}</p>
                  </div>
                  <div>
                    <span className="text-gray-400">Camera Y:</span>
                    <p className="text-primary-400 font-bold">{cameraValues.y.toFixed(2)}</p>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-3">
                  📋 Open DevTools (F12 → Console) to see live control logs
                </p>
              </section>
            </>
          ) : controlPanelPage === 'actions' ? (
            <section className="cp-actions">
              <h2 className="text-xl font-bold text-primary-400 mb-2">Action Buttons</h2>
              <p className="text-sm text-gray-400 mb-6">
                Room and environment controls. Select device above first.
              </p>

              {categoriesWithButtons.map(({ category, buttons: catButtons }) => (
                <div key={category.id} className="cp-actions-category">
                  <h3 className="cp-actions-category-title">{category.name}</h3>
                  <div className="cp-actions-grid">
                    {catButtons.map((button) => (
                      <button
                        key={button.id}
                        type="button"
                        className="cp-action-btn"
                        onClick={() => handleActionButton(button)}
                      >
                        {button.name}
                      </button>
                    ))}
                  </div>
                </div>
              ))}

              {safeCategories.length === 0 && (
                <p className="text-gray-500 text-sm">No action categories configured.</p>
              )}
            </section>
                    ) : (
            <section className="cp-actions">
              <div style={{ maxWidth: '72rem', margin: '0 auto' }}>
                <h2 className="text-xl font-bold text-primary-400 mb-3">Floor Plan Teleport</h2>
                <p className="text-sm text-gray-400 mb-5">
                  Tap on teleport points on the map to move in VR. Select a device above first.
                </p>

                <div className="cp-floor-switch">
                  <button
                    type="button"
                    className="cp-floor-switch-btn"
                    onClick={goToPreviousFloor}
                    disabled={selectedFloorIndex <= 0}
                    title="Previous floor"
                    aria-label="Previous floor"
                  >
                    {'<'}
                  </button>
                  <span
                    className="cp-floor-switch-label"
                    title={
                      selectedFloorPlan
                        ? `${selectedFloorPlan.name} (${selectedFloorIndex + 1}/${safeFloorPlans.length})`
                        : 'No floor selected'
                    }
                  >
                    {selectedFloorPlan
                      ? `${selectedFloorPlan.name} (${selectedFloorIndex + 1}/${safeFloorPlans.length})`
                      : 'No floor selected'}
                  </span>
                  <button
                    type="button"
                    className="cp-floor-switch-btn"
                    onClick={goToNextFloor}
                    disabled={selectedFloorIndex < 0 || selectedFloorIndex >= safeFloorPlans.length - 1}
                    title="Next floor"
                    aria-label="Next floor"
                  >
                    {'>'}
                  </button>
                </div>

                {!selectedFloorPlan ? (
                  <p className="text-gray-500 text-sm">
                    No floor plan configured for this project. Ask an admin to add one in the
                    Projects - Floor Plan page.
                  </p>
                ) : (
                  <>
                    <div
                      className="floorplan-container relative inline-block w-full max-w-full bg-gray-900 rounded-lg overflow-hidden border border-gray-700"
                    >
                      <img
                        src={resolveBackendAssetUrl(selectedFloorPlan.imageUrl)}
                        style={{ width: '100%', height: 'auto', display: 'block' }}
                        alt={selectedFloorPlan.name}
                      />
                      {selectedFloorPoints.map((point) => {
                        const hasWorld =
                          point.worldX != null &&
                          point.worldY != null &&
                          point.worldZ != null &&
                          !Number.isNaN(point.worldX) &&
                          !Number.isNaN(point.worldY) &&
                          !Number.isNaN(point.worldZ);
                        const canTeleport = Boolean(selectedProjectId && selectedDevice && hasWorld);
                        const title = !hasWorld
                          ? 'No world coordinates set for this point'
                          : !selectedDevice
                            ? 'Select a device in Session first'
                            : point.name;
                        const onClick = () => {
                          if (canTeleport) handleTeleportPointClick(point);
                        };
                        if (point.iconUrl) {
                          return (
                            <button
                              key={point.id}
                              type="button"
                              disabled={!canTeleport}
                              style={{
                                position: 'absolute',
                                left: `${point.mapX * 100}%`,
                                top: `${point.mapY * 100}%`,
                                transform: 'translate(-50%, -50%)',
                                border: 'none',
                                background: 'transparent',
                                padding: 0,
                                cursor: canTeleport ? 'pointer' : 'not-allowed',
                                opacity: canTeleport ? 1 : 0.55,
                              }}
                              onClick={onClick}
                              title={title}
                            >
                              <img
                                src={resolveBackendAssetUrl(point.iconUrl)}
                                alt={point.name}
                                style={{
                                  width: 32,
                                  height: 32,
                                  borderRadius: '9999px',
                                  border: '2px solid white',
                                  boxShadow: '0 0 6px rgba(0,0,0,0.6)',
                                  opacity: canTeleport ? 1 : 0.7,
                                }}
                              />
                            </button>
                          );
                        }
                        return (
                          <button
                            key={point.id}
                            type="button"
                            disabled={!canTeleport}
                            className={`rounded-full floorplan-point text-xs font-semibold shadow-sm${canTeleport ? '' : ' floorplan-point--disabled'}`}
                            style={{
                              position: 'absolute',
                              left: `${point.mapX * 100}%`,
                              top: `${point.mapY * 100}%`,
                              transform: 'translate(-50%, -50%)',
                              padding: '0.3rem 0.45rem',
                              minWidth: '1.7rem',
                            }}
                            onClick={onClick}
                            title={title}
                          >
                            {point.name.charAt(0).toUpperCase()}
                          </button>
                        );
                      })}
                    </div>
                    {selectedFloorPoints.length === 0 && (
                      <p className="text-gray-500 text-sm mt-3">
                        No teleport points configured for this floor yet.
                      </p>
                    )}
                  </>
                )}
              </div>
            </section>
          )}
        </div>
      </main>
    </div>
  );
};

export default ControlPanel;

